/**
 * The drag handle between the chart and the positions panel on the desktop trade page, and the hook that
 * keeps the panel's height: 28% of the trade column (180–240px) until the trader drags it, then whatever
 * they chose, remembered on this device. Collapsed leaves the account bar and the tab strip; the chart
 * takes the rest.
 */
import { useCallback, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { cx } from './ui';

const KEY = 'tradeSplit';
/** Panel height limits (px, or a share of the trade column), the chart's minimum above it, the keyboard step. */
export const SPLIT = { min: 160, maxShare: 0.6, chartMin: 300, defaultShare: 0.28, defaultMin: 180, defaultMax: 240, step: 16, handle: 7 };

interface Saved {
  h?: unknown;
  collapsed?: unknown;
}
function read(): Saved {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Saved) : {};
  } catch {
    return {};
  }
}
function save(h: number, collapsed: boolean) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ h: Math.round(h), collapsed }));
  } catch {
    /* storage unavailable: the split lasts for this visit */
  }
}
const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/**
 * Panel height and collapsed state, bounded by the column's height (`column`) and the chart's position in
 * it (`chart`). `active` is false on the phone layout, where neither element exists.
 */
export function useSplit(column: RefObject<HTMLElement | null>, chart: RefObject<HTMLElement | null>, active: boolean) {
  const [h, setHState] = useState(() => {
    const s = read().h;
    return typeof s === 'number' && Number.isFinite(s) && s > 0 ? s : 0; // 0: not chosen yet, the default applies
  });
  const [collapsed, setCollapsedState] = useState(() => read().collapsed === true);
  const [max, setMax] = useState(Number.MAX_SAFE_INTEGER);
  const cur = useRef({ h, collapsed, max });
  cur.current = { h, collapsed, max };

  // Bounds follow the column's height (and the banners above the chart); the first visit gets the default.
  useLayoutEffect(() => {
    const col = column.current;
    if (!active || !col) return;
    const measure = () => {
      const colH = col.clientHeight;
      const top = chart.current ? chart.current.getBoundingClientRect().top - col.getBoundingClientRect().top : 0;
      const m = Math.max(SPLIT.min, Math.min(colH * SPLIT.maxShare, colH - top - SPLIT.chartMin - SPLIT.handle));
      setMax(m);
      setHState((x) => clamp(x || clamp(colH * SPLIT.defaultShare, SPLIT.defaultMin, SPLIT.defaultMax), SPLIT.min, m));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(col);
    if (chart.current) ro.observe(chart.current);
    return () => ro.disconnect();
  }, [active, column, chart]);

  const setH = useCallback((next: number) => {
    const v = clamp(next, SPLIT.min, cur.current.max);
    setHState(v);
    save(v, cur.current.collapsed);
  }, []);
  const setCollapsed = useCallback((c: boolean) => {
    setCollapsedState(c);
    save(cur.current.h, c);
  }, []);
  return { h, max, collapsed, setH, setCollapsed };
}

/** 7px handle: drag to resize, double-click to collapse or expand, arrow keys / Home / End from the keyboard. */
export function Splitter({ h, max, collapsed, onChange, onCollapse }: { h: number; max: number; collapsed: boolean; onChange: (h: number) => void; onCollapse: (collapsed: boolean) => void }) {
  const drag = useRef<{ y: number; h: number } | null>(null);
  const [active, setActive] = useState(false);

  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setActive(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    document.body.style.cursor = '';
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="horizontal"
      aria-label="Resize the positions panel"
      aria-valuemin={0}
      aria-valuemax={Math.round(max)}
      aria-valuenow={collapsed ? 0 : Math.round(h)}
      title="Drag to resize. Double-click to collapse or expand."
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { y: e.clientY, h: collapsed ? 0 : h };
        setActive(true);
        document.body.style.cursor = 'row-resize';
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const next = d.h + (d.y - e.clientY);
        if (collapsed) {
          // Pulling a collapsed panel up opens it once the drag is half the minimum height.
          if (next < SPLIT.min / 2) return;
          onCollapse(false);
        }
        onChange(clamp(next, SPLIT.min, max));
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => onCollapse(!collapsed)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') {
          if (collapsed) onCollapse(false);
          else onChange(clamp(h + SPLIT.step, SPLIT.min, max));
        } else if (e.key === 'ArrowDown') {
          if (!collapsed) onChange(clamp(h - SPLIT.step, SPLIT.min, max));
        } else if (e.key === 'Home') onCollapse(true);
        else if (e.key === 'End') {
          if (collapsed) onCollapse(false);
          else onChange(max);
        } else return;
        e.preventDefault();
      }}
      className={cx('group relative h-[7px] shrink-0 cursor-row-resize touch-none border-t border-line select-none', active ? 'bg-accent-bg' : 'hover:bg-accent-bg')}
    >
      <span className={cx('pointer-events-none absolute top-1/2 left-1/2 h-0.5 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full', active ? 'bg-accent' : 'bg-line-strong group-hover:bg-accent')} />
    </div>
  );
}
